/**
 * module: SLTSnow_api.js
 * author: Rob Blanch
 * created: 12/10/2020
 * license: see https://www.stonylanetech.com/licenses/Snow/license.txt for details
 * 
 * (c) 2024 Stony Lane Tech LLC
 */
import sltDateTime from './sltDateTime.js';
import sltIDKeyTypeHelper from './sltIDKeyTypeHelper.js';
import versionfile from "./version";
import SLTSnowAppErrHelper from './SLTSnowAppErrHelper.js';
import { startRegistration } from '@simplewebauthn/browser';
import { startAuthentication, WebAuthnAbortService } from '@simplewebauthn/browser';

const mode = 'cors';

/**
 * SLTSnow_api
 *  this is the main set of routines for accessing REST APIs of
 * Snow web service.
 */
class SLTSnow_api {
    constructor() {
        this.app_key = window.SLTSNOW_APP_KEY;
        this.session = null;
        this.usesCookieSession = false;
        this.listeners = [];
        this.headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        };

        this.sltDateTime = new sltDateTime();
        this.sltIDKeyTypeHelper = new sltIDKeyTypeHelper();
    }

    fetchApi(url, options = {}) {
        const applicationApi = this.getApiUrl('session');
        const requestUrl = new URL(url, window.location.href);
        const credentials = applicationApi && requestUrl.origin === applicationApi.origin
            ? 'include' : 'same-origin';
        return fetch(url, {...options, credentials}).then(response => {
            if (this.usesCookieSession && (response.status === 401 || response.status === 403)) {
                this.clearSessionCache();
                throw new Error('SLTSnow_api::getSessionID() session is no longer valid.');
            }
            return response;
        });
    }

    // This flag carries no credential; the server validates the HttpOnly cookie.
    useCookieSession() {
        this.clearSessionCache();
        this.usesCookieSession = true;
    }

    restoreCookieSession(onComplete, onError) {
        this.fetchApi(this.getApiUrl('session'))
            .then(response => response.json())
            .then(response => {
                if (!response || response.user_key === undefined || response.user_key === null) {
                    throw new Error('SLTSnow_api::getSessionID() no authenticated cookie session.');
                }
                this.useCookieSession();
                onComplete({});
            })
            .catch(onError);
    }

    /**
     * DefaultAlias
     *  return a default alias string for the user
     */
    static DefaultAlias(firstName, lastName) {
        var firstCharLastName = (lastName && lastName.length) ? lastName.charAt(0) : '';
        return (firstName && firstName.length > 0) ? firstName + firstCharLastName : '';
    }

    /**
     * LowerCaseCompareStrings
     *  perform string comparison of strings as lower case (ie. case insensitive),
     * and return comparison result equivalent to what an JS array sort
     * comparator function expects.
     */
    static LowerCaseCompareStrings(firstString, secondString) {
        var result = 0;

        if (firstString && secondString) {
            var firstStringLower = firstString.toLowerCase();
            var secondStringLower = secondString.toLowerCase();
            result = (firstStringLower < secondStringLower) ? -1 : ((firstStringLower > secondStringLower) ? 1 : 0);
        } else if (firstString) {
            result = 1;
        } else if (secondString) {
            result = -1;
        }

        return result;
    }

    /**
     * ComparePropertyNames
     *  used to sort properties by name with array.sort() capabilities in js
     */
    static ComparePropertyNames(firstProperty, secondProperty) {
        var prop1Name = (firstProperty && ("property_name" in firstProperty) === true) ? firstProperty.property_name : null;
        var prop2Name = (secondProperty && ("property_name" in secondProperty) === true) ? secondProperty.property_name : null;
        var result = SLTSnow_api.LowerCaseCompareStrings(prop1Name, prop2Name);

        return result;
    }

    /**
     * SortPropertiesByName
     *  return sorted properties by name (sorted in place). it is recommended to call
     * this routine for alpha sorting of properties prior to handling in table.
     */
    static SortPropertiesByName(properties) {
        if (properties) {
            if (("length" in properties) === true && properties.length) {
                properties.sort(SLTSnow_api.ComparePropertyNames);
            }
        }

        return properties;
    }

    /**
     * FindPropertyIndexByName
     *  return the index of the property matching name within this.state.properties. -1 if not found
     * will be returned.
     */
    static FindPropertyIndexByName(properties, property_name) {
        var idxFound = null;

        if (properties) {
            for (var idx = 0; idx < properties.length; idx++) {
                if (property_name === properties[idx].property_name) {
                    idxFound = idx;
                    break;
                }
            }    
        }

        return (idxFound !== null) ? idxFound : -1;
    }

    /**
     * DoesPropertyExist
     *  Is the property existing within the provided properties. true if exists, false if not
     */
    static DoesPropertyExist(properties, property_name) {
        var isProperty = false;
        var found_property_index = SLTSnow_api.FindPropertyIndexByName(properties, property_name);

        if (found_property_index >= 0) {
            isProperty = true;
        }

        return isProperty;
    }

    /**
     * PropertyValueForPropertyName
     *  provided a property set and a property name, return the property value. returns null if not found or missing property_value.
     */
    static PropertyValueForPropertyName(properties, property_name) {
        var idx = SLTSnow_api.FindPropertyIndexByName(properties, property_name);
        return (idx > -1) ? (("property_value" in properties[idx]) ? properties[idx].property_value : null) : null;
    }

    /**
     * buildProperUrlString
     *  take into account possible missing '/' on parts of URL. null if
     * error occurs.
     */
    buildProperUrlString(baseUri, apiName) {
        var url = null;

        if (baseUri && apiName && baseUri.length && apiName.length) {
            baseUri = (baseUri[baseUri.length-1] === '/') ? baseUri : baseUri.concat('/');
            url = baseUri + apiName;
        }

        return url;
    }

    /**
     * getApiUrl
     *  used to construct basic root api call given an api name for REST API
     */
    getApiUrl(apiName)
    {
        var uri = null;

        try {
            uri = this.buildProperUrlString(window.BASE_SLTSNOW_API, apiName);
        } catch (error) {
            console.log('SLTSnow_api.getApiUrl() [ERROR]: Exception encountered. error=' + JSON.stringify(error));
        }

        return (uri) ? new URL(uri) : null;    
    }

    /**
     * getSLTApiUrl
     *  used to get authentication service root api call given name for REST API
     */
    getSLTApiUrl(apiName)
    {
        var uri = null;

        try {
            uri = this.buildProperUrlString(window.BASE_SLTAUTH_API, apiName);
        } catch (error) {
            console.log('SLTSnow_api.getSLTApiUrl() [ERROR]: Exception encountered. error=' + JSON.stringify(error));
        }

        return (uri) ? new URL(uri) : null;
    }

    /**
     * postApplication
     *  used to create an application (if appropriate params supplied -- see doc)
     */
    postApplication(onComplete, onError, registrationKey, owner, label)
    {
        const apiUrl = this.getApiUrl('application');
        var body = JSON.stringify({ registration_key: registrationKey, 
                                    owner: owner, 
                                    label: label});
        var request =   {   method: 'POST',
                            mode: mode,
                            headers: this.headers,
                            body: body};

        this.fetchApi( apiUrl, request )
        .then (response => response.json())
        .then ( (response) => 
                            {
                                if (onComplete !== null) {
                                    onComplete(response);
                                }
                            },
                (error) => 
                        { 
                            if (onError !== null) {
                                onError({message: 'application POST Error: ' + error.message});
                            }
                        });

        return;
    }

    /**
     * getVersion
     *  returns version information for REST APIs
     */
    getVersion(onComplete, onError)
    {
        try {
            this.fetchApi(versionfile)
                .then(r => r.text())
                .then(text => {
                    var siteversion = text.toString();

                    const apiUrl = this.getApiUrl('version');
                    this.fetchApi(apiUrl)
                        .then(res => res.json())
                        .then(
                            (result) => {
                                result.version = siteversion;
                                onComplete(result);
                            },
                            (error) => {
                                onError(error);
                            }
                        )
                        .catch(error => {
                            onError(error);
                        });
                })
                .catch(error => {
                    onError(error);
                });
        } catch (error) {
            onError(error);
        }

        return;
    }

    /**
     * setSessionCache
     *  sets session information for cache purposes. should only be used by calls within this module of code (ie. private)
     */
    setSessionCache(session)
    {
        var listener_type;
        var old_session = this.session;
        var new_session = session;
        var param1;
        var param2;

        if (session) {
            this.usesCookieSession = false;
            sessionStorage.setItem("session", JSON.stringify(session));
        } else {
            sessionStorage.removeItem("session");
        }
        sessionStorage.removeItem("user_alias");        /* force a re-load upon request in getUserAlias() */
        this.session = session;

        if (old_session && new_session) {
            listener_type = 'session-refresh';
            param1 = new_session;
            param2 = old_session;
        } else if (new_session) {
            listener_type = 'session-login';
            param1 = new_session;
        } else {
            listener_type = 'session-logout';
            param1 = old_session;
        }

        if (listener_type) {
            var listeners = (this.listeners && this.listeners[listener_type]) ? this.listeners[listener_type] : null;

            for (var idxListener = 0; listeners && idxListener < listeners.length; idxListener++) {
                var listener = listeners[idxListener];
                if (listener) {
                    listener(param1, param2);
                }
            }
        }
    }

    /**
     * getSessionCache
     *  called to retrieve the session from local object cache or fall back on session storage
     */
    getSessionCache() {
        var session = null;

        if (this.doesSessionExist()) {
            session = this.session;
        } else {
            session = JSON.parse(sessionStorage.getItem("session"));
            if (session) {
                this.session = session;
            }
        }

        return session;
    }

    /**
     * doesSessionExist
     *      called to detect whether or not a session exists yet
     */
    doesSessionExist() {
        return this.usesCookieSession || !!this.session;
    }

    /**
     * clearSessionCache
     *  called to remove the session from the cache
     */
    clearSessionCache() {
        this.usesCookieSession = false;
        this.setSessionCache(null);
    }

    /**
     * registerListener
     *  used to register a listener for an event generated by sltorangeapi
     * returns index of listener added for event starting with zero
     */
    registerListener(event, addListener) {
        var index = 0;

        if (this.listeners && this.listeners[event]) {
            this.listeners[event].push(addListener);
            index = this.listeners[event].length;
        } else {
            var listeners = [ addListener ];
            this.listeners[event] = listeners;
        }

        return index;
    }

    /**
     * unregisterListener
     *  used to remove a listener for an event of sltorangeapi
     * returns true if listener found & removed; false if not
     */
    unregisterListener(event, removeListener) {
        var listeners = (this.listeners) ? this.listeners[event] : null;
        var newListeners = null;
        var fRemoved = false;

        for (var idx = 0; listeners && idx < listeners.length; idx++) {
            var listener = listeners[idx];
            if (listener && listener === removeListener) {
                fRemoved = true;
                continue;
            }

            if (newListeners === null) {
                newListeners[0] = listener;
            } else {
                newListeners.push(listener);
            }
        }

        this.listeners[event] = newListeners;

        return fRemoved;
    }

    /**
     * getSession
     *  will call onComplete with session id information and onError if there is an issue. identifier & password are required if first session being obtained.
     */
    getSession(onComplete, onError, identifier = null, password = null)
    {
        if ((!identifier && !password) || (this.usesCookieSession && !password)) {
            if (this.usesCookieSession) {
                onComplete({});
                return;
            }
            if (!this.session && !this.getSessionCache()) {
                this.restoreCookieSession(onComplete, onError);
                return;
            }
        }
        const app_key = this.app_key;
        var body = null;
        var id_key_type = null;
        
        if (identifier) {
            id_key_type = this.sltIDKeyTypeHelper.id_key_type(identifier);
        } else if (this.session) {
            identifier = this.session.identifier;
            id_key_type = this.sltIDKeyTypeHelper.id_key_type(identifier);
        } else {
            var session = this.getSessionCache();
            if (session) {
                identifier = this.session.identifier;
                id_key_type = this.sltIDKeyTypeHelper.id_key_type(identifier);
            }
        }

        /**
         * make sure we picked up identifier & id_key_type somehow (either cached or supplied)
         */
        if (identifier === null || id_key_type === null) {
            if (identifier === null) {
                onError({message: 'SLTSnow_api::getSessionID() no identifier can be found to obtain session.'});
            }
    
            if (id_key_type === null) {
                onError({message: 'SLTSnow_api::getSessionID() no id_key_type was discovered for identifier to obtain session. might be parsing identifier issue.'});
            }    
        } else {
            /**
             *  1. If we have session cached, test to see if we need to obtain new sessionid, or can use existing cached sessionid
             *      a. use "witching window" which is time period before expiration to provide buffer for execution with session id by app
             *      b. if sessionid is expired or going to be expired within "witching window" then construct JSON body to refresh the session id
             *  2. If no session cached, construct JSON body with app_key, identifier, and password to obtain first session with service
             *  3. If body exists, execute REST API POST call to obtain new session
             *      a. cache session and call onComplete upon successful fetch of new session information
             *      b. call onError if REST API call not successful 
             *  4. If no body and cached session exists
             *      a. if onComplete supplied, then call it
             *      b. if no onComplete just return to caller
             *  5. If no REST API CALL in step 3 and no cached session for step 4 and have onError defined, call it with error info
             */
            if (this.session !== null) {
                const witchingWindow = 120*1000;    /* # milliseconds within expirationdate to grab new session */
                var witchingDate = this.sltDateTime.parse(this.session.expirationdate, true)-witchingWindow;
                var utcNow = this.sltDateTime.utcNow();

                if (witchingDate <= utcNow) {
                    body = JSON.stringify({sessionid:this.session.sessionid, refreshtoken:this.session.refreshtoken, id_key_type: id_key_type, identifier:this.session.identifier});
                }
            } else if (app_key !== null && identifier !== null && password !== null) {
                body = JSON.stringify({app_key: app_key, id_key_type: id_key_type, identifier: identifier, password: password});
            }
            
            if (body !== null) {
                const apiUrl = this.getSLTApiUrl('sessionid');
                var request =   {
                                method: 'POST',
                                mode: mode,
                                headers: this.headers,
                                body: body,
                                };

                this.fetchApi( apiUrl, request )
                .then (response => response.json())
                .then ( (response) => {
                                        if (response.sessionid != null) {
                                            response.identifier = identifier;
                                            this.setSessionCache(response);
                                            if (onComplete !== null) {
                                                onComplete(response);
                                            }
                                        } else {
                                            var errHelper = new SLTSnowAppErrHelper();
                                            var errors = errHelper.getErrorsInMessage(response.errormessage);
                                            if (errors && errors.length > 0 && 
                                                (errors[0] === "SLT_PROC_ERR [10000]" || errors[0] === "SLT_PROC_ERR [10072]")) {
                                                // session appears to be invalid - clear session cache
                                                this.clearSessionCache();
                                            }

                                            if (onError !== null) {
                                                onError({message: JSON.stringify(response)});
                                            }
                                        }
                                    },
                        (error) => { 
                                        if (onError !== null) {
                                            onError({message: 'sessionid POST Error: ' + error.message});
                                        }
                                    });
            } else if (this.session) {
                if (onComplete !== null) {
                    /** cached session is good to return **/
                    onComplete(this.session);
                }
            } else {
                onError({message: 'SLTSnow_api::getSessionID() no cached session && missing mandatory app_key, identifier, or password parameter to obtain session.'});
            }
        }

        return;
    }

    /**
     * deleteSession
     *  called to log a user out of a session.
     */
    deleteSession(onComplete, onError)
    {
        this.getSession(
            (session) => {
                const apiUrl = this.usesCookieSession ? this.getApiUrl('session') : this.getSLTApiUrl('sessionid');
                var request =   {
                                method: 'DELETE',
                                mode: mode,
                                headers: this.headers,
                                body: JSON.stringify({sessionid: (session && 'sessionid' in session) ? session.sessionid : null}),
                                };
        
                this.fetchApi( apiUrl, request )
                .then ( response => response.json() )
                .then ( (response) => {
                                        if (response.success === true) {
                                            /** need to null out session cache, since no longer valid **/
                                            this.clearSessionCache();

                                            if (onComplete !== null) {
                                                onComplete(response);
                                            }
                                        } else {
                                            if (onError !== null) {
                                                onError({message: JSON.stringify(response)});
                                            }
                                        }
                                    },
                        (error) => { 
                                        if (onError !== null) {
                                            onError({message: 'session DELETE Error: ' + error.message});
                                        }
                                    },
                                );
            },
            (error) => {
                if (onError !== null) {
                    onError(error);
                }
            }
        )
    }

    /**
     * getUserAlias
     *  used to obtain alias of user (leverages cache by default).
     * will call onComplete with alias string or null if there is an error obtaining the user's alias
     */
    getUserAlias(onComplete, fUseCache = true) {
        var alias = sessionStorage.getItem("user_alias");

        if ((alias === null || alias === undefined || alias === '') || fUseCache === false) {
            this.getUser(
                        (userinfo) => {
                            sessionStorage.setItem("user_alias", userinfo.alias);
                            onComplete(userinfo.alias);
                            },
                        (error) => {
                            console.log('SLTSnow_api::getUserAlias() [ERROR]: failed to obtain user alias from service. error.message=' + error.message);
                            sessionStorage.setItem("user_alias", null);
                            onComplete(null);
                        });
        } else {
            onComplete(alias);
        }
    }

    /**
     * getUser
     *  used to obtain user information from JME4 REST service
     */
    getUser(onComplete, onError)
    {
        var convertUserLotsArrayToLotNumberString = this.convertUserLotsArrayToLotNumberString.bind(this);

        /**
         *  1. obtain session (or throw error if not possible)
         *  2. construct user API GET request and if not error return results using onComplete callback
         */
        this.getSession(
            (session) => {
                var url = this.getApiUrl('user');
                const params = new URLSearchParams({
                    ...(session.sessionid ? {sessionid: session.sessionid} : {}),
                });
                url.search = params.toString();

                this.fetchApi(url)
                .then (response => response.json())
                .then ( (response) => { 
                                        if (response.user_key !== null) {
                                            response.lotNumber = convertUserLotsArrayToLotNumberString(response.user_lots);
                                            onComplete(response);
                                        } else {
                                            onError(response);
                                        }
                                    },
                        (error) => { 
                                        onError({message: 'user GET Error: ' + error.message});
                                    })
            },
            (error) => {
                onError(error);
            }
        )
    }

    /**
     * convertUserLotsArrayToLotNumberString
     * used to convert user lots array into comma delimited lot number string
     */
    convertUserLotsArrayToLotNumberString(userLots) {
        var lotNumberString = '';

        if (userLots && userLots.length) {
            for (var idx = 0; idx < userLots.length; idx++) {
                var lotNumber = userLots[idx].lotnumber;

                if (lotNumberString.length) {
                    lotNumberString = lotNumberString + ', ';
                }
                
                lotNumberString = lotNumberString + lotNumber;
            }
        }

        return lotNumberString;
    }

    /**
     * convertLotNumberStringToUserLotsArray
     * used to convert comma delimited lot number string into user lots array
     */
    convertLotNumberStringToUserLotsArray(neighborhood, lotNumberString) {
        var userLots = [];

        if (lotNumberString && lotNumberString.length) {
            var lotNumbers = lotNumberString.split(',');

            for (var idx = 0; idx < lotNumbers.length; idx++) {
                var lotNumber = lotNumbers[idx].trim();

                if (lotNumber.length) {
                    userLots.push({ neighborhood: neighborhood, lotnumber: lotNumber });
                }
            }
        }

        return userLots;
    }

    /**
     * createUser
     *  used to register a new user account
     */
    createUser(onComplete, onError, neighborhood, lotNumber, firstName, lastName, alias, identifier, password)
    {
        const apiUrl = this.getApiUrl('user');
        var id_key_type = this.sltIDKeyTypeHelper.id_key_type(identifier);
        var user_lots = this.convertLotNumberStringToUserLotsArray(neighborhood, lotNumber);
        var request =   {
                        method: 'POST',
                        mode: mode,
                        headers: this.headers,
                        body: JSON.stringify({  app_key: this.app_key,
                                                neighborhood: neighborhood,
                                                firstname: firstName, 
                                                lastname: lastName, 
                                                alias: alias, 
                                                user_lots: user_lots,
                                                id_key_type: id_key_type, 
                                                identifier: identifier, 
                                                password: password}),
                        };

        this.fetchApi( apiUrl, request )
        .then (response => response.json())
        .then ( (response) => {
                                if (response.user_key !== null) {
                                    if (onComplete !== null) {
                                        onComplete(response);
                                    }
                                } else {
                                    if (onError !== null) {
                                        onError({message: JSON.stringify(response)});
                                    }
                                }
                            },
                (error) => { 
                                if (onError !== null) {
                                    onError({message: 'user POST Error: ' + error.message});
                                }
                            });
    }

    /**
     * updateUser
     *  used to update an existing user account with new information in jme4
     */
    updateUser(onComplete, onError, neighborhood, lotNumber, firstName, lastName, alias, password)
    {
        this.getSession(
            (session) => {
                const apiUrl = this.getApiUrl('user');
                var user_lots = this.convertLotNumberStringToUserLotsArray(neighborhood, lotNumber);
                var request =   {
                                method: 'POST',
                                mode: mode,
                                headers: this.headers,
                                body: JSON.stringify({sessionid: session.sessionid, 
                                                     neighborhood: neighborhood, 
                                                     user_lots: user_lots,
                                                     firstname: firstName, 
                                                     lastname: lastName, 
                                                     alias: alias, 
                                                     password: password}),
                                };
        
                this.fetchApi( apiUrl, request )
                .then ( response => response.json() )
                .then ( (response) => {
                                        if (response.user_key) {
                                            if (onComplete !== null) {
                                                onComplete(response);
                                            }
                                        } else {
                                            if (onError !== null) {
                                                onError({message: JSON.stringify(response)});
                                            }
                                        }
                                    },
                        (error) => { 
                                        if (onError !== null) {
                                            onError({message: 'user POST Error: ' + error.message});
                                        }
                                    },
                                );
            },
            (error) => {
                if (onError !== null) {
                    onError(error);
                }
            }
        )
    }

    /**
     * persistUserProperties
     *  stash the user properties with the service.
     * should_put flag is used to specify whether all user properties are to be posted
     * for user in this call, or only the properties being supplied should be put for the user.
     * if should_put is true, then only properties supplied will be added or modified for user.
     * if should_put is false, then user_properties will be applied to user as if it represents
     * the complete set of user properties, so existing properties are removed and then the
     * user_properties are persisted for the user in this scenario.
     */
    persistUserProperties(onComplete, onError, should_put, user_properties) {
        this.getSession(
            (session) => {
                const apiUrl = this.getApiUrl('user');
                var request =   {
                                method: 'POST',
                                mode: mode,
                                headers: this.headers,
                                body: JSON.stringify({sessionid: session.sessionid, put_properties: should_put, user_properties: user_properties}),
                                };
        
                this.fetchApi( apiUrl, request )
                .then ( response => response.json() )
                .then ( (response) => {
                                        if (response.user_key) {
                                            if (onComplete !== null) {
                                                onComplete(response);
                                            }
                                        } else {
                                            if (onError !== null) {
                                                onError({message: JSON.stringify(response)});
                                            }
                                        }
                                    },
                        (error) => { 
                                        if (onError !== null) {
                                            onError({message: 'user properties POST Error: ' + error.message});
                                        }
                                    },
                                );
            },
            (error) => {
                if (onError !== null) {
                    onError(error);
                }
            }
        );
    }

    /**
     * validateAccount
     *  called to validate a user account for usage (note: need validationkey sent to user identifier account)
     */
    validateAccount(onComplete, onError, identifier, validationkey, password)
    {
        const apiUrl = this.getSLTApiUrl('validationkey');
        var id_key_type = this.sltIDKeyTypeHelper.id_key_type(identifier);
        var request =   {
                        method: 'POST',
                        mode: mode,
                        headers: this.headers,
                        body: JSON.stringify({id_key_type: id_key_type, identifier: identifier, validationkey: validationkey, password: password}),
                        };

        this.fetchApi( apiUrl, request )
        .then ( response => response.json() )
        .then ( (response) => {
                                if (response.success === true) {
                                    if (onComplete !== null) {
                                        onComplete(response);
                                    }
                                } else {
                                    if (onError !== null) {
                                        onError({message: 'Failed activation attempt for account ' + identifier + '. Service returned: ' + JSON.stringify(response)});
                                    }
                                }
                            },
                (error) => { 
                                if (onError !== null) {
                                    onError({message: 'validationkey POST Error: ' + error.message});
                                }
                            },
                        );
    }

    /**
     * removeAccount
     *  called in response to remove key to approve user account removal
     */
    removeAccount(onComplete, onError, identifier, removeKey, password)
    {
        const apiUrl = this.getSLTApiUrl('removekey');
        var id_key_type = this.sltIDKeyTypeHelper.id_key_type(identifier);
        var request =   {
                        method: 'POST',
                        mode: mode,
                        headers: this.headers,
                        body: JSON.stringify({id_key_type: id_key_type, identifier: identifier, removekey: removeKey, password: password}),
                        };

        this.fetchApi( apiUrl, request )
        .then ( response => response.json() )
        .then ( (response) => {
                                if (response.success === true) {
                                    if (onComplete !== null) {
                                        onComplete(response);
                                    }
                                } else {
                                    if (onError !== null) {
                                        onError({message: 'Failed remove account attempt for account ' + identifier + '. Service returned: ' + JSON.stringify(response)});
                                    }
                                }
                            },
                (error) => { 
                                if (onError !== null) {
                                    onError({message: 'removekey POST Error: ' + error.message});
                                }
                            },
                );
    }

    /**
     * resetPassword
     *  reset the password for user account
     */
    resetPassword(onComplete, onError, identifier, resetKey, newPassword)
    {
        const apiUrl = this.getSLTApiUrl('resetkey');
        var id_key_type = this.sltIDKeyTypeHelper.id_key_type(identifier);
        var request =   {
                        method: 'POST',
                        mode: mode,
                        headers: this.headers,
                        body: JSON.stringify({id_key_type: id_key_type, identifier: identifier, resetkey: resetKey, new_password: newPassword}),
                        };

        this.fetchApi( apiUrl, request )
        .then ( response => response.json() )
        .then ( (response) => {
                                if (response.success === true) {
                                    if (onComplete !== null) {
                                        onComplete(response);
                                    }
                                } else {
                                    if (onError !== null) {
                                        onError({message: 'Failed reset password attempt for account ' + identifier + '. Service returned: ' + JSON.stringify(response)});
                                    }
                                }
                            },
                (error) => { 
                                if (onError !== null) {
                                    onError({message: 'reset POST Error: ' + error.message});
                                }
                            },
                );
    }

    /**
     * getUserPasskeys
     *  used to obtain the passkeys for which user is allowed
     */
    getUserPasskeys(onComplete, onError) {
        this.getSession(
            (session) => {
                var apiUrl = (this.usesCookieSession ? this.getApiUrl('passkey') : this.getSLTApiUrl('passkey'));
                const params = new URLSearchParams({
                    ...(session.sessionid ? {sessionid: session.sessionid} : {}),
                });
                apiUrl.search = params.toString();

                this.fetchApi( apiUrl )
                .then ( response => response.json() )
                .then ( (response) => {
                                        if (response !== undefined && response!== null && response.passkeys !== undefined && response.passkeys !== null) {
                                            if (onComplete !== null) {
                                                onComplete(response);
                                            }
                                        } else {
                                            if (onError !== null) {
                                                onError({message: 'Failed passkeys attempt. Service returned: ' + JSON.stringify(response)});
                                            }
                                        }
                                    },
                        (error) => { 
                                        if (onError !== null) {
                                            onError({message: 'passkeys GET Error: ' + error.message});
                                        }
                                    },
                        );
        });
    }

    /**
     * postRegistrationPasskeyOptions
     *  used to post registration passkey options for which user is allowed
     */
    postRegistrationPasskeyOptions(onComplete, onError) {
        this.getSession(
            (session) => {
                const apiUrl = this.getApiUrl('registrationpasskeyoptions');
                var request =   {
                                method: 'POST',
                                mode: mode,
                                headers: this.headers,
                                body: JSON.stringify({sessionid: session.sessionid}),
                                };

                this.fetchApi( apiUrl, request )
                .then ( response => response.json() )
                .then ( (response) => {
                                        if (response !== undefined && response!== null && response.challenge !== undefined && response.challenge !== null && response.challenge.length > 0) {
                                            if (onComplete !== null) {
                                                onComplete(response);
                                            }
                                        } else {
                                            if (onError !== null) {
                                                onError({message: 'Failed passkeyoptions attempt. Service returned: ' + JSON.stringify(response)});
                                            }
                                        }
                                    },
                        (error) => { 
                                        if (onError !== null) {
                                            onError({message: 'passkeyoptions POST Error: ' + error.message});
                                        }
                                    },
                        );
        });
    }

    /**
     * verifyPasskeyRegistration
     *  used to verify the passkey registration for which user is allowed
     */
    verifyPasskeyRegistration(attestationResponse, onComplete, onError) {
        this.getSession(
            (session) => {
                const apiUrl = this.getApiUrl('registrationpasskeyverify');
                var request =   {
                                method: 'POST',
                                mode: mode,
                                headers: this.headers,
                                body: JSON.stringify({sessionid: session.sessionid, attestationResponse: attestationResponse}),
                                };

                this.fetchApi( apiUrl, request )
                .then ( response => response.json() )
                .then ( (response) => {
                                        if (response.verified === true) {
                                            if (onComplete !== null) {
                                                onComplete(response);
                                            }
                                        } else {
                                            if (onError !== null) {
                                                onError({message: 'Failed passkeyverify attempt. Service returned: ' + JSON.stringify(response)});
                                            }
                                        }
                                    },
                        (error) => { 
                                        if (onError !== null) {
                                            onError({message: 'passkeyverify POST Error: ' + error.message});
                                        }
                                    }
                        );
            }
        );
    }

    /**
     * createPasskey
     *  callback routine when user creates a new passkey for their account
     */
    async createPasskey(onComplete, onError) {
        this.postRegistrationPasskeyOptions(
            async (response) => {
                if (response && response.challenge !== undefined && response.challenge !== null && response.challenge.length > 0) {
                    /**
                     * now need to utilize options to generate a new passkey for user and then post the new passkey to the service for storage
                     * 1. call navigator.credentials.create() with options to generate new passkey for user
                     * 2. post the new passkey to the service for storage   
                     */
                    var attResp = null;
                    try {
                        attResp = await startRegistration({optionsJSON: response});
                    } catch (error) {
                        if (onError !== null) {
                            onError({message: 'Failed to create passkey: ' + error.message});
                        }
                        return;
                    }

                    if (attResp === null) {
                        if (onError !== null) {
                            onError({message: 'Failed to create passkey: attestation response is null'});
                        }
                        return;
                    }

                    /**
                     * verifyPasskeyRegistration will post the new passkey to the service for storage and return success or failure
                     */
                    this.verifyPasskeyRegistration(
                        attResp,
                        async (response) => {
                            if (response && response.verified === true) {
                                if (onComplete !== null) {
                                    onComplete(response);
                                }
                            } else {
                                if (onError !== null) {
                                    onError({message: 'Failed to verify passkey registration. Service returned: ' + JSON.stringify(response)});
                                }
                            }
                        },
                        (error) => { 
                            if (onError !== null) {
                                onError({message: 'Failed to verify passkey registration: ' + error.message});
                            }
                        });
                } else {
                    if (onError !== null) {
                        onError({message: 'Failed passkeyoptions attempt. Service returned: ' + JSON.stringify(response)});
                    }
                }
            },
            (error) => { 
                if (onError !== null) {
                    onError({message: 'passkeyoptions POST Error: ' + error.message});
                }
            }
        );
    }

    /**
     * deletePasskey
     *  used to delete a passkey for which user is allowed
     */
    deletePasskey(onComplete, onError, credential_id) {
        this.getSession(
            (session) => {
                const apiUrl = (this.usesCookieSession ? this.getApiUrl('passkey') : this.getSLTApiUrl('passkey'));
                var request =   {
                                method: 'DELETE',
                                mode: mode,
                                headers: this.headers,
                                body: JSON.stringify({sessionid: session.sessionid, credential_id: credential_id}),
                                };
        
                this.fetchApi( apiUrl, request )
                .then ( response => response.json() )
                .then ( (response) => {
                                        if (response.success === true) {
                                            if (onComplete !== null) {
                                                onComplete(response);
                                            }
                                        } else {
                                            if (onError !== null) {
                                                onError({message: 'Failed delete passkey attempt. Service returned: ' + JSON.stringify(response)});
                                            }
                                        }
                                    },
                        (error) => { 
                                        if (onError !== null) {
                                            onError({message: 'passkey DELETE Error: ' + error.message});
                                        }
                                    }
                                );
            },
            (error) => {
                if (onError !== null) {
                    onError({message: 'passkey DELETE Error: ' + error.message});
                }
            }
        )
    }

    /**
     * postPasskeyAuthenticationOptions
     *  used to post passkey authentication options for which user in process to authenticate a user
     */
    postPasskeyAuthenticationOptions(app_key, identifier, onComplete, onError, requestOptions = {}) {
        const url = this.getApiUrl('authenticationpasskeyoptions');

        var request =   {
                        method: 'POST',
                        mode: mode,
                        headers: this.headers,
                        body: JSON.stringify({app_key: app_key, identifier: identifier}),
                        };

        this.fetchApi(url, {...request, ...requestOptions})
        .then ( response => response.json() )
        .then ( (response) => {
                                if (response && response.challenge !== undefined && response.challenge !== null && response.challenge.length > 0) {
                                    onComplete(response);
                                } else {
                                    var errHelper = new SLTSnowAppErrHelper();
                                    var errMsg = errHelper.userPresentableError(response);

                                    if (errMsg === null || errMsg === undefined || errMsg.length === 0) {
                                        errMsg = 'passkey authentication options POST Error: ' + JSON.stringify(response);
                                    }

                                    onError({message: errMsg});
                                }
                            },
                (error) => { 
                                onError({message: 'passkey authentication options POST Error: ' + error.message});
                            },
                        );
    }

    /**
     * postPasskeyAuthenticationVerify
     *  used to post passkey authentication verify for which user in process to authenticate a user
     */
    postPasskeyAuthenticationVerify(app_key, identifier, authenticationResponse, onComplete, onError, requestOptions = {}) {
        const url = this.getApiUrl('authenticationpasskeyverify');
        var request =   {
                        method: 'POST',
                        mode: mode,
                        headers: this.headers,
                        body: JSON.stringify({app_key: app_key, identifier: identifier, authenticationResponse: authenticationResponse}),
                        };

        this.fetchApi(url, {...request, ...requestOptions})
        .then ( response => response.json() )
        .then ( (response) => {
                                if (response && response.success === true) {
                                    this.useCookieSession();
                                    onComplete(response);
                                } else {
                                    onError({message: 'passkey authentication verify POST Error: ' + JSON.stringify(response)});
                                }
                            },
                (error) => { 
                                onError({message: 'passkey authentication verify POST Error: ' + error.message});
                            },
                        );
    }

    /**
     * passkeyAuthenticate
     *      routine to authenticate a user with passkey. handles calls to obtain
     * options and verify authentication response with service. onComplete callback will be called 
     * with response from service if successful, otherwise onError callback will be called with error message.
     * 
     * note: sessionid will be a browser session cookie if successful, and service and
     * browser will maintain session for user until browser session is closed or user logs out.
     */
    getSignInMethods(onComplete, onError, identifier) {
        this.fetchApi(this.getApiUrl('authenticationmethods'), {
            method: 'POST', headers: this.headers,
            body: JSON.stringify({app_key: this.app_key, identifier}),
        }).then(response => response.json()).then(response => {
            if (typeof response.hasPasskeys !== 'boolean') throw new Error('Unable to check sign-in options.');
            onComplete(response);
        }).catch(onError);
    }

    discoverablePasskeyAuthenticate(onComplete, onError, {useBrowserAutofill = false, onSelected = () => {}} = {}) {
        const controller = new AbortController();
        let active = true;
        const post = async (endpoint, body) => {
            const response = await this.fetchApi(this.getApiUrl(endpoint), {
                method: 'POST', headers: this.headers, signal: controller.signal,
                body: JSON.stringify({app_key: this.app_key, ...body}),
            });
            const result = await response.json();
            if (response.status >= 400) throw new Error(result.message || 'Passkey sign-in failed.');
            return result;
        };
        (async () => {
            try {
                const options = await post('discoverablepasskeyoptions', {});
                if (!active) return;
                const assertion = await startAuthentication({optionsJSON: options, useBrowserAutofill});
                if (!active) return;
                onSelected();
                if (!active) return;
                const result = await post('discoverablepasskeyverify', {authenticationResponse: assertion});
                if (!active) return;
                if (result.success !== true) throw new Error('Passkey sign-in failed.');
                this.useCookieSession();
                onComplete(result);
            } catch (error) { if (active) onError(error); }
        })();
        return () => {
            active = false;
            controller.abort();
            WebAuthnAbortService.cancelCeremony();
        };
    }

    passkeyAuthenticate(identifier, onComplete, onError) {
        const controller = new AbortController();
        let active = true;
        const fail = error => { if (active) onError(error); };
        this.postPasskeyAuthenticationOptions(this.app_key, identifier, async options => {
            if (!active) return;
            try {
                const response = await startAuthentication({optionsJSON: options});
                if (!active) return;
                this.postPasskeyAuthenticationVerify(this.app_key, identifier, response,
                    result => { if (active) onComplete(result); }, fail, {signal: controller.signal});
            } catch (error) { fail(error); }
        }, fail, {signal: controller.signal});
        return () => {
            active = false;
            controller.abort();
            WebAuthnAbortService.cancelCeremony();
        };
    }

    /**
     * postLotOwners
     * used to post lot owners for which user is allowed
     */
    postLotOwners(onComplete, onError, lots) {
        this.getSession(
            (session) => {
                const url = this.getApiUrl('lot_owners');

                var request =   {
                                method: 'POST',
                                mode: mode,
                                headers: this.headers,
                                body: JSON.stringify({sessionid: session.sessionid, lots: lots}),
                                };
        
                this.fetchApi( url, request )
                .then ( response => response.json() )
                .then ( (response) => {
                                        if (response && response.success === true) {
                                            onComplete(response);
                                        } else {
                                            onError({message: 'lot owners POST Error: ' + JSON.stringify(response)});
                                        }
                                    },
                        (error) => { 
                                        onError({message: 'lot owners POST Error: ' + error.message});
                                    },
                                );
            },
            (error) => {
                onError(error);
            }
        )
    }

    /**
     * lotOwners
     * return list of lot owners which user is allowed to view
     */
    getLotOwners(onComplete, onError) {
        this.getSession(
            (session) => {
                const url = this.getApiUrl('lot_owners');
                const params = new URLSearchParams({
                    ...(session.sessionid ? {sessionid: session.sessionid} : {}),
                });

                url.search = params.toString();

                this.fetchApi(url)
                .then (response => response.json())
                .then ( (response) => {
                            onComplete(response);
                        },
                        (error) => {
                            onError(error);
                        })
            },
            (error) => {
                onError(error);
            }
        )
    }

    /**
     * getLotsAsGeoJSON
     * return lot information in GeoJSON format
     */
    getLotsAsGeoJSON(onComplete, onError) {
        this.getSession(
            (session) => {
                const url = this.getApiUrl('lots');
                const params = new URLSearchParams({
                    ...(session.sessionid ? {sessionid: session.sessionid} : {}),
                });

                url.search = params.toString();

                this.fetchApi(url)
                .then (response => response.json())
                .then ( (response) => {
                            const geoJSON = this.convertLotsToGeoJSON(response);
                            onComplete(geoJSON);
                        },
                        (error) => {
                            onError(error);
                        })
            },
            (error) => {
                onError(error);
            }
        )
    }

    /**
     * getUsageStatistics
     *  return information regarding usage statistics of service
     */
    getUsageStatistics(onComplete, onError) {
        this.getSession(
            (session) => {
                const url = this.getApiUrl('usagestatistics');
                const params = new URLSearchParams({
                    ...(session.sessionid ? {sessionid: session.sessionid} : {}),
                });

                url.search = params.toString();

                this.fetchApi(url)
                .then (response => response.json())
                .then ( (response) => {
                            onComplete(response);
                        },
                        (error) => {
                            onError(error);
                        })
            },
            (error) => {
                onError(error);
            }
        )
    }

    /**
     * parseSimpleCSV
     *  simple CSV parser to convert CSV text into array of objects.
     * note returned data structure:
     *  {
     *      headers: [ 'header1', 'header2', ... ],
     *      rows: [ { header1: 'value1', header2: 'value2', ... },
     *              { header1: 'value1', header2: 'value2', ... },
     *              ...
     *            ]
     *  }
     */
    parseSimpleCSV(csvText) {
        if (!csvText || csvText.length === 0) {
            return null;
        }

        var lines = csvText.split('\n');
        var headers = lines[0].split(',');
        headers.forEach((header, index) => {
            headers[index] = header.trim();
        });

        var data = {
            headers: headers,
            rows: []
        };

        for (var i = 1; i < lines.length; i++) {
            var obj = {};
            var currentline = lines[i].split(',');

            if (currentline === undefined || currentline === null || currentline.length !== headers.length) {
                continue;
            }

            for (var j = 0; j < headers.length; j++) {
                obj[headers[j].trim()] = currentline[j] ? currentline[j].trim() : '';
            }

            data.rows.push(obj);
        }

        return data;
    }

    /*
     * convertCSVContentToLotDataPayload
     *  convert parsed CSV content into lot data payload for POST lots service call
     */
    convertCSVContentToLotDataPayload(parsedCSVContent, defaultCity='Horseshoe Bay', defaultState='TX', defaultZip='78657', defaultCountry='USA') {
        var lotDataPayload = [];

        if (parsedCSVContent && parsedCSVContent.headers && parsedCSVContent.rows) {
            const LotNumber = parsedCSVContent.headers.find(header => header.toLowerCase() === 'lot');
            const Address = parsedCSVContent.headers.find(header => header.toLowerCase() === 'address');
            const CornerLat = parsedCSVContent.headers.find(header => header.toLowerCase() === 'corner lat');
            const CornerLong = parsedCSVContent.headers.find(header => header.toLowerCase() === 'corner long');

            if (!LotNumber || !Address || !CornerLat || !CornerLong) {
                return null;
            }

            var lastLotNumber = null;
            var lotDataEntry = null;
            for (var idxRow = 0; idxRow < parsedCSVContent.rows.length; idxRow++) {
                var row = parsedCSVContent.rows[idxRow];
                var lotNumber = row[LotNumber];
                var lot_coordinate = {
                    corner_lat: row[CornerLat],
                    corner_long: row[CornerLong]
                }

                if (lotDataEntry !== null && lastLotNumber !== lotNumber) {
                    lotDataPayload.push(lotDataEntry);
                    lotDataEntry = null;
                }

                if (lastLotNumber !== lotNumber) {
                    lotDataEntry = {
                        lot_number: lotNumber,
                        lot_address: {
                            address_line1: row[Address],
                            address_line2: '',
                            city: defaultCity,
                            state_province_region: defaultState,
                            postal_code: defaultZip,
                            country: defaultCountry
                        },
                        lot_coordinates: [ lot_coordinate ]
                    }
                    lastLotNumber = lotNumber;
                } else {
                    lotDataEntry.lot_coordinates.push(lot_coordinate);
                }
            }

            if (lotDataEntry !== null) {
                lotDataPayload.push(lotDataEntry);
            }
        }

        return lotDataPayload;
    }

    /**
     * loadLotDataToService
     * load lot data into system
     */
    loadLotDataToService(onComplete, onError, neighborhood, lotDataFilename) {
        const reader = new FileReader();

        /** setup reader to handle completion of loading the file **/
        reader.onload = (event) => {
            const lotDataContent = event.target.result;
            const parsedCSVContent = this.parseSimpleCSV(lotDataContent);
            const lotDataPayload = this.convertCSVContentToLotDataPayload(parsedCSVContent);

            if (lotDataPayload !== null) {
                this.getSession(
                    (session) => {
                        const apiUrl = this.getApiUrl('lots');
                        var request =   {
                                        method: 'POST',
                                        mode: mode,
                                        headers: this.headers,
                                        body: JSON.stringify({ sessionid: session.sessionid,
                                                               neighborhood: neighborhood,
                                                               lots: lotDataPayload }),
                                        };
                
                        this.fetchApi( apiUrl, request )
                        .then ( response => response.json() )
                        .then ( (response) => {
                                                if (response.lotcount === lotDataPayload.length) {
                                                    if (onComplete !== null) {
                                                        onComplete(response.lotcount+' lots successfully loaded.');
                                                    }
                                                } else {
                                                    if (onError !== null) {
                                                        onError({message: JSON.stringify(response)});
                                                    }
                                                }
                                            },
                            (error) => { 
                                            if (onError !== null) {
                                                onError({message: 'load lot data POST Error: ' + error.message});
                                            }
                                        },
                                    );
                    },
                    (error) => {
                        if (onError !== null) {
                            onError(error);
                        }
                    }
                );
            }
        }

        /** set reader to handle errors during file reading **/
        reader.onerror = (event) => {
            if (onError !== null) {
                onError({message: 'Error reading lot data file: ' + event.target.error.message});
            }
        }

        /** start reading the lot data file **/
        reader.readAsText(lotDataFilename);
    }

    /**
     * loadLotData
     * load POA data into system
     */
    loadLotData(onComplete, onError, neighborhood, lotDataFilename) {
        if ((lotDataFilename !== null && lotDataFilename !== '')) {
            this.loadLotDataToService(
                (success) => {
                    if (onComplete !== null) {
                        onComplete(success);
                    }
                },
                (error) => {
                    if (onError !== null) {
                        onError(error);
                    }
                },
                neighborhood,
                lotDataFilename
            );
        }
    }

    /**
     * parseOwnerNames
     *  parse owner names from CSV content into array of owner name strings
     */
    parseOwnerNames(ownerNamesCSV) {
        var name1;
        var name2;
        var ownerNames = [];

        if (ownerNamesCSV && ownerNamesCSV.length) {
            var nameparts = ownerNamesCSV.split(' ');

            /** handle no name **/
            if (nameparts.length === 0) {
                return ownerNames;
            }

            /** handle one or two part names as a singular owner **/
            if (nameparts.length <= 2) {
                ownerNames.push(ownerNamesCSV.trim());
                return ownerNames;
            }

            /** handle two first names with single last name with 'and' or '&'  **/
            if ((nameparts[1].toLowerCase() === 'and' || nameparts[1].toLowerCase() === '&') &&
                (nameparts.length === 4)) {
                name1 = nameparts[0] + ' ' + nameparts[3];
                name2 = nameparts[2] + ' ' + nameparts[3];
                ownerNames.push(name1.trim());
                ownerNames.push(name2.trim());
                return ownerNames;
            }

            /** handle two different first/last name with separation by 'and' or '&' **/
            if ((nameparts[2].toLowerCase() === 'and' || nameparts[2].toLowerCase() === '&') &&
                (nameparts.length === 5)) {
                name1 = nameparts[0] + ' ' + nameparts[1];
                name2 = nameparts[3] + ' ' + nameparts[4];
                ownerNames.push(name1.trim());
                ownerNames.push(name2.trim());
                return ownerNames;
            }

            /**
             * default treat string as a single owner
             */
            ownerNames.push( ownerNamesCSV.trim() );
        }

        return ownerNames;
    }

    /**
     * parsePhoneNumbers
     *  parse phone numbers from CSV content into array of phone number strings
     */
    parsePhoneNumbers(phoneNumbersCSV) {
        var phoneNumbers = [];

        if (phoneNumbersCSV && phoneNumbersCSV.length) {
            var numbers = phoneNumbersCSV.split(';');

            for (var idx = 0; idx < numbers.length; idx++) {
                var number = numbers[idx].trim();

                if (number.length) {
                    phoneNumbers.push(number);
                }
            }
        }

        return phoneNumbers;
    }

    /**
     * parseEmailAddresses
     *  parse email addresses from CSV content into array of email address strings
     */
    parseEmailAddresses(emailAddressCSV) {
        var emailAddresses = [];

        if (emailAddressCSV && emailAddressCSV.length) {
            var emails = emailAddressCSV.split(';');

            for (var idxEmail = 0; idxEmail < emails.length; idxEmail++) {
                var email = emails[idxEmail].trim();

                if (email.length) {
                    emailAddresses.push(email);
                }
            }
        }

        return emailAddresses;
    }

    /**
     ** parseMailingAddresses
     **  parse mailing address components from CSV content into array of mailing address objects
     **/
    parseMailingAddresses(addressLine1, city, state, zip) {
        var mailingAddresses = [];

        addressLine1 = addressLine1 ? addressLine1.trim() : '';
        city = city ? city.trim() : '';
        state = state ? state.trim() : '';
        zip = zip ? zip.trim() : '';

        if (addressLine1.length || city.length || state.length || zip.length) {
            var mailingAddress = {
                address_line1: addressLine1,
                address_line2: '',
                city: city,
                state_province_region: state,
                postal_code: zip,
                country: 'USA'
            };

            mailingAddresses.push(mailingAddress);
        }

        return mailingAddresses;
    }

    /**
     * convertCSVContentToOwnerDataPayload
     *  convert parsed CSV content into owner data payload for POST lot_owners service call
     */
    convertCSVContentToOwnerDataPayload(neighborhood, parsedCSVContent) {
        var ownerDataPayload = [];
        var parseOwnerNames = this.parseOwnerNames.bind(this);
        var parsePhoneNumbers = this.parsePhoneNumbers.bind(this);
        var parseEmailAddresses = this.parseEmailAddresses.bind(this);
        var parseMailingAddresses = this.parseMailingAddresses.bind(this);

        if (parsedCSVContent && parsedCSVContent.headers && parsedCSVContent.rows) {
            const LotNumber = parsedCSVContent.headers.find(header => header.toLowerCase() === 'lot');
            const HomeOwners = parsedCSVContent.headers.find(header => header.toLowerCase() === 'home owners');
            const Phone = parsedCSVContent.headers.find(header => header.toLowerCase() === 'phone');
            const EmailAddress = parsedCSVContent.headers.find(header => header.toLowerCase() === 'email address');
            const SpouseEmail = parsedCSVContent.headers.find(header => header.toLowerCase() === 'spouse\'s email');
            const AddressLine1 = parsedCSVContent.headers.find(header => header.toLowerCase() === 'add1');
            const City = parsedCSVContent.headers.find(header => header.toLowerCase() === 'city');
            const State = parsedCSVContent.headers.find(header => header.toLowerCase() === 'state');
            const Zip = parsedCSVContent.headers.find(header => header.toLowerCase() === 'zip');

            if (!LotNumber || !HomeOwners || !Phone || !EmailAddress || !SpouseEmail) {
                return null;
            }

            /**
             * go lot by lot and owners with phone numbers and email addresses
             */
            for (var idxRow = 0; idxRow < parsedCSVContent.rows.length; idxRow++) {
                var row = parsedCSVContent.rows[idxRow];
                var lotOwnerEntry = {
                    neighborhood: neighborhood,
                    lot_number: row[LotNumber],
                    owners: []
                };

                var ownerNames = parseOwnerNames(row[HomeOwners]);
                var phoneNumbers = parsePhoneNumbers(row[Phone]);
                var emailAddresses = parseEmailAddresses(row[EmailAddress]);
                var spouseEmailAddresses = parseEmailAddresses(row[SpouseEmail]);
                var mailingAddresses = parseMailingAddresses(row[AddressLine1], row[City], row[State], row[Zip]);
                var allEmailAddresses = (Array.isArray(emailAddresses) && Array.isArray(spouseEmailAddresses)) ? emailAddresses.concat(spouseEmailAddresses) : emailAddresses;
                
                for (var idxOwner = 0; idxOwner < ownerNames.length; idxOwner++) {
                    var ownerEntry = {
                        owner_name: ownerNames[idxOwner],
                        phone_numbers: [],
                        email_addresses: [],
                        mailing_addresses: []
                    };

                    /** add phone numbers for owner (note: assume single one per owner in order) **/
                    if (phoneNumbers.length > idxOwner) {
                        var phoneNumber = {
                            phone_number_type: 'primary',
                            phone_number: phoneNumbers[idxOwner]
                        };

                        ownerEntry.phone_numbers.push(phoneNumber);
                    }
                    
                    /** add email addresses for owner (note: assume single one per owner in order) **/
                    if (allEmailAddresses.length > idxOwner) {
                        var emailAddress = {
                            email_address_type: 'primary',
                            email_address: allEmailAddresses[idxOwner]
                        };

                        ownerEntry.email_addresses.push(emailAddress);
                    }

                    if (mailingAddresses.length > idxOwner) {
                        var mailingAddress = {
                            address_type: 'primary',
                            address_line1: mailingAddresses[idxOwner].address_line1,
                            address_line2: mailingAddresses[idxOwner].address_line2,
                            city: mailingAddresses[idxOwner].city,
                            state_province_region: mailingAddresses[idxOwner].state_province_region,
                            postal_code: mailingAddresses[idxOwner].postal_code,
                            country: mailingAddresses[idxOwner].country
                        };

                        ownerEntry.mailing_addresses.push(mailingAddress);
                    }

                    lotOwnerEntry.owners.push(ownerEntry);
                }

                ownerDataPayload.push(lotOwnerEntry);
            }
        }

        return ownerDataPayload;
    }

    /**
     * loadOwnerDataToService
     * load owner POA data into system
     */
    loadOwnerDataToService(onComplete, onError, neighborhood, lotOwnerFilename) {
        const reader = new FileReader();

        /** setup reader to handle completion of loading the file **/
        reader.onload = (event) => {
            const ownerDataContent = event.target.result;
            const parsedCSVContent = this.parseSimpleCSV(ownerDataContent);
            const ownerDataPayload = this.convertCSVContentToOwnerDataPayload(neighborhood, parsedCSVContent);

            if (ownerDataPayload !== null) {
                this.getSession(
                    (session) => {
                        const apiUrl = this.getApiUrl('lot_owners');
                        var request =   {
                                        method: 'POST',
                                        mode: mode,
                                        headers: this.headers,
                                        body: JSON.stringify({ sessionid: session.sessionid,
                                                               lots: ownerDataPayload }),
                                        };
                
                        this.fetchApi( apiUrl, request )
                        .then ( response => response.json() )
                        .then ( (response) => {
                                                    if (response.success === true) {
                                                    if (onComplete !== null) {
                                                        onComplete(' lot owners successfully loaded.');
                                                    }
                                                } else {
                                                    if (onError !== null) {
                                                        onError({message: JSON.stringify(response)});
                                                    }
                                                }
                                            },
                            (error) => { 
                                            if (onError !== null) {
                                                onError({message: 'load owner data POST Error: ' + error.message});
                                            }
                                        },
                                    );
                    },
                    (error) => {
                        if (onError !== null) {
                            onError(error);
                        }
                    }
                );
            }
        }

        /** set reader to handle errors during file reading **/
        reader.onerror = (event) => {
            if (onError !== null) {
                onError({message: 'Error reading lot data file: ' + event.target.error.message});
            }
        }

        /** start reading the lot data file **/
        reader.readAsText(lotOwnerFilename);
    }

    /**
     * loadOwnerData
     * load owner POA data into system
     */
    loadOwnerData(onComplete, onError, neighborhood, lotOwnerFilename) {
        if (lotOwnerFilename !== null && lotOwnerFilename !== '') {
            this.loadOwnerDataToService(
                (success) => {
                    if (onComplete !== null) {
                        onComplete(success);
                    }
                },
                (error) => {
                    if (onError !== null) {
                        onError(error);
                    }
                },
                neighborhood,
                lotOwnerFilename
            );
        }
    }

    /**
     * convertLotsToGeoJSON
     * return array of geoJSON features collection format for lot info
     */
    convertLotsToGeoJSON(lotInfo) {
        var featureCollection = {
            type: "FeatureCollection",
            features: []
        };

        if (lotInfo && Array.isArray(lotInfo.lots)) {
            lotInfo.lots.forEach( (lot) => {
                if (lot.lot_coordinates && Array.isArray(lot.lot_coordinates)) {
                    var coordinates = [];
                    lot.lot_coordinates.forEach( (coord) => {
                        coordinates.push( [ parseFloat(coord.corner_long), parseFloat(coord.corner_lat) ] );
                    });

                    var feature = {
                        type: "Feature",
                        properties: {
                            lotId: lot.lot_number,
                            lotLabel: lot.lot_address.address_line1,
                            ownersDisplay: (lot.owners !== undefined && lot.owners !== null) ? lot.owners.map( (owner) => owner.owner_name ).join(', ') : '',
                        },
                        geometry: {
                            type: "Polygon",
                            coordinates: [ coordinates ]
                        }
                    };

                    featureCollection.features.push(feature);
                }
            });
        }

        return featureCollection;
    }
}

export default SLTSnow_api;